export const precalculusLessons = [
  {
    id: 'precalc-1',
    title: 'Polynomial Functions',
    unit: 'Advanced Functions',
    description: 'Master polynomial functions including zeros, end behavior, factoring, and graphing techniques',
    standards: ['CCSS.MATH.CONTENT.HSF.IF.C.7', 'NCTM: Algebra', 'Ohio: PC.F.1', 'NY: AI-F.IF.7'],
    vocabulary: [
      { term: 'Polynomial', definition: 'An expression consisting of variables and coefficients with non-negative integer exponents' },
      { term: 'Degree', definition: 'The highest power of the variable in a polynomial' },
      { term: 'Leading Coefficient', definition: 'The coefficient of the term with the highest degree' },
      { term: 'Zero/Root', definition: 'A value of x where the polynomial equals zero' },
      { term: 'End Behavior', definition: 'The behavior of a function as x approaches positive or negative infinity' },
      { term: 'Multiplicity', definition: 'The number of times a particular root appears' },
      { term: 'Turning Point', definition: 'A point where the function changes from increasing to decreasing or vice versa' },
      { term: 'Synthetic Division', definition: 'A shorthand method for dividing polynomials' }
    ],
    concepts: [
      'Polynomial functions have the form f(x) = aₙxⁿ + aₙ₋₁xⁿ⁻¹ + ... + a₁x + a₀',
      'The degree determines the maximum number of zeros and turning points',
      'End behavior is determined by the leading term (degree and coefficient)',
      'The Fundamental Theorem of Algebra states a polynomial of degree n has exactly n complex zeros',
      'Rational Root Theorem helps identify possible rational zeros',
      'Descartes\' Rule of Signs predicts the number of positive and negative real zeros'
    ],
    examples: [
      {
        problem: 'Find all zeros of f(x) = x³ - 6x² + 11x - 6',
        steps: [
          'Use Rational Root Theorem: possible rational roots are ±1, ±2, ±3, ±6',
          'Test x = 1: f(1) = 1 - 6 + 11 - 6 = 0 ✓ (x = 1 is a zero)',
          'Use synthetic division to factor: (x - 1)(x² - 5x + 6)',
          'Factor the quadratic: (x - 1)(x - 2)(x - 3)',
          'Zeros are x = 1, 2, 3'
        ]
      },
      {
        problem: 'Describe the end behavior of f(x) = -2x⁴ + 3x³ - x + 5',
        steps: [
          'Identify the leading term: -2x⁴',
          'Degree is 4 (even), leading coefficient is -2 (negative)',
          'As x → +∞, f(x) → -∞',
          'As x → -∞, f(x) → -∞',
          'Both ends point downward'
        ]
      }
    ],
    teacherTip: 'Use graphing technology to help students visualize how multiplicity affects the graph at zeros. A zero with odd multiplicity crosses the x-axis, while even multiplicity touches and turns around.',
    questions: [
      {
        id: 'poly-1',
        question: 'What is the degree of the polynomial f(x) = 3x⁵ - 2x³ + 7x - 1?',
        options: ['3', '5', '7', '-1'],
        correctAnswer: 1,
        explanation: 'The degree is the highest power of x, which is 5.'
      },
      {
        id: 'poly-2',
        question: 'If a polynomial has degree 6, what is the maximum number of turning points?',
        options: ['4', '5', '6', '7'],
        correctAnswer: 1,
        explanation: 'A polynomial of degree n has at most n-1 turning points. For degree 6: 6-1 = 5.'
      },
      {
        id: 'poly-3',
        question: 'What is the end behavior of f(x) = -x⁶ + 2x³ - 5?',
        options: ['Both ends up', 'Both ends down', 'Left up, right down', 'Left down, right up'],
        correctAnswer: 1,
        explanation: 'Leading term is -x⁶ (even degree, negative coefficient), so both ends go down.'
      },
      {
        id: 'poly-4',
        question: 'Using the Rational Root Theorem, which is a possible rational zero of f(x) = 2x³ - 5x² + 3x - 6?',
        options: ['x = 4', 'x = 3/2', 'x = 5', 'x = 7/2'],
        correctAnswer: 1,
        explanation: 'Possible rational roots are ±(factors of 6)/(factors of 2) = ±1, ±2, ±3, ±6, ±1/2, ±3/2.'
      },
      {
        id: 'poly-5',
        question: 'If (x - 3) is a factor of f(x), what is f(3)?',
        options: ['3', '-3', '0', 'Cannot determine'],
        correctAnswer: 2,
        explanation: 'By the Factor Theorem, if (x - a) is a factor, then f(a) = 0. So f(3) = 0.'
      },
      {
        id: 'poly-6',
        question: 'What is the result of (x³ - 8) ÷ (x - 2)?',
        options: ['x² + 2x + 4', 'x² - 2x + 4', 'x² + 4', 'x² - 4'],
        correctAnswer: 0,
        explanation: 'Using synthetic division or long division: (x³ - 8) ÷ (x - 2) = x² + 2x + 4.'
      },
      {
        id: 'poly-7',
        question: 'A polynomial has zeros at x = -2, 1, and 4. Which could be the polynomial?',
        options: ['f(x) = (x+2)(x-1)(x-4)', 'f(x) = (x-2)(x+1)(x+4)', 'f(x) = x³ - 3x² - 6x + 8', 'Both A and C'],
        correctAnswer: 3,
        explanation: 'Both forms represent the same polynomial with the given zeros.'
      },
      {
        id: 'poly-8',
        question: 'What is the multiplicity of the zero x = 2 in f(x) = (x - 2)³(x + 1)?',
        options: ['1', '2', '3', '4'],
        correctAnswer: 2,
        explanation: 'The factor (x - 2) appears 3 times, so the multiplicity is 3.'
      },
      {
        id: 'poly-9',
        question: 'How does the graph behave at a zero with even multiplicity?',
        options: ['Crosses the x-axis', 'Touches and turns around', 'Has a vertical asymptote', 'Has a hole'],
        correctAnswer: 1,
        explanation: 'At zeros with even multiplicity, the graph touches the x-axis and turns around.'
      },
      {
        id: 'poly-10',
        question: 'What is the remainder when f(x) = x⁴ - 3x² + 2 is divided by (x - 1)?',
        options: ['-2', '0', '2', '4'],
        correctAnswer: 1,
        explanation: 'By the Remainder Theorem, the remainder is f(1) = 1 - 3 + 2 = 0.'
      },
      {
        id: 'poly-11',
        question: 'Which polynomial has the same end behavior as f(x) = -3x⁷ + 5x⁴ - 2x + 1?',
        options: ['g(x) = -3x⁷', 'g(x) = 5x⁴', 'g(x) = 3x⁷', 'g(x) = -2x'],
        correctAnswer: 0,
        explanation: 'End behavior is determined by the leading term: -3x⁷.'
      },
      {
        id: 'poly-12',
        question: 'If f(x) = x³ - 4x² + x + 6 and f(2) = 0, what is one factor?',
        options: ['(x + 2)', '(x - 2)', '(x + 3)', '(x - 3)'],
        correctAnswer: 1,
        explanation: 'If f(2) = 0, then (x - 2) is a factor by the Factor Theorem.'
      },
      {
        id: 'poly-13',
        question: 'What is the maximum number of real zeros for f(x) = x⁴ - 5x² + 4?',
        options: ['2', '3', '4', '5'],
        correctAnswer: 2,
        explanation: 'A polynomial of degree 4 can have at most 4 real zeros.'
      },
      {
        id: 'poly-14',
        question: 'Using Descartes\' Rule of Signs, how many positive real zeros can f(x) = x³ - 2x² + x - 3 have?',
        options: ['0 or 2', '1 or 3', '3 or 1', '2 or 0'],
        correctAnswer: 2,
        explanation: 'There are 3 sign changes, so there are 3 or 1 positive real zeros.'
      },
      {
        id: 'poly-15',
        question: 'What is the y-intercept of f(x) = 2x³ - 5x² + 3x - 7?',
        options: ['2', '-5', '3', '-7'],
        correctAnswer: 3,
        explanation: 'The y-intercept is f(0) = -7.'
      },
      {
        id: 'poly-16',
        question: 'If f(x) = (x - 1)²(x + 2), what is the degree?',
        options: ['2', '3', '4', '5'],
        correctAnswer: 1,
        explanation: 'Expanding gives x³ + ..., so the degree is 3.'
      },
      {
        id: 'poly-17',
        question: 'Which statement about polynomial division is true?',
        options: ['The remainder must be zero', 'The remainder degree &lt; divisor degree', 'The quotient degree &gt; dividend degree', 'Division always results in a polynomial'],
        correctAnswer: 1,
        explanation: 'The remainder must have a degree less than the divisor.'
      },
      {
        id: 'poly-18',
        question: 'What is the leading coefficient of f(x) = 5 - 2x + 3x² - x³?',
        options: ['5', '-2', '3', '-1'],
        correctAnswer: 3,
        explanation: 'Rewrite in standard form: -x³ + 3x² - 2x + 5. Leading coefficient is -1.'
      },
      {
        id: 'poly-19',
        question: 'If a cubic polynomial has 3 distinct real zeros, how many turning points does it have?',
        options: ['0', '1', '2', '3'],
        correctAnswer: 2,
        explanation: 'A cubic (degree 3) has at most 2 turning points, and with 3 distinct zeros, it has exactly 2.'
      },
      {
        id: 'poly-20',
        question: 'What is the sum of the zeros of f(x) = x³ - 6x² + 11x - 6?',
        options: ['6', '-6', '11', '-11'],
        correctAnswer: 0,
        explanation: 'By Vieta\'s formulas, the sum of zeros equals -b/a = -(-6)/1 = 6.'
      },
      {
        id: 'poly-21',
        question: 'Which polynomial has a zero at x = 2 with multiplicity 2?',
        options: ['f(x) = (x - 2)', 'f(x) = (x - 2)²', 'f(x) = 2(x - 2)', 'f(x) = x² - 2'],
        correctAnswer: 1,
        explanation: 'Multiplicity 2 means the factor (x - 2) appears twice: (x - 2)².'
      },
      {
        id: 'poly-22',
        question: 'What is the product of the zeros of f(x) = 2x³ - 8x² + 6x?',
        options: ['0', '3', '-3', '6'],
        correctAnswer: 0,
        explanation: 'Factor: 2x(x² - 4x + 3) = 2x(x-1)(x-3). Zeros: 0, 1, 3. Product: 0×1×3 = 0.'
      },
      {
        id: 'poly-23',
        question: 'If f(x) is a polynomial and f(-2) = 0, which is a factor?',
        options: ['(x + 2)', '(x - 2)', '(-x + 2)', '(-x - 2)'],
        correctAnswer: 0,
        explanation: 'If f(-2) = 0, then (x - (-2)) = (x + 2) is a factor.'
      },
      {
        id: 'poly-24',
        question: 'What is the degree of the quotient when dividing x⁵ by x²?',
        options: ['2', '3', '5', '7'],
        correctAnswer: 1,
        explanation: 'x⁵ ÷ x² = x³, which has degree 3.'
      },
      {
        id: 'poly-25',
        question: 'Which polynomial has exactly 2 turning points?',
        options: ['Degree 1', 'Degree 2', 'Degree 3', 'Degree 4'],
        correctAnswer: 2,
        explanation: 'A polynomial of degree 3 has at most 2 turning points.'
      },
      {
        id: 'poly-26',
        question: 'What is the constant term of f(x) = (x - 1)(x + 2)(x - 3)?',
        options: ['6', '-6', '5', '-5'],
        correctAnswer: 0,
        explanation: 'The constant term is (-1)(2)(-3) = 6.'
      },
      {
        id: 'poly-27',
        question: 'If f(x) = x⁴ - 16, what are all the real zeros?',
        options: ['±2', '±4', '2 only', '4 only'],
        correctAnswer: 0,
        explanation: 'x⁴ - 16 = (x² - 4)(x² + 4) = (x-2)(x+2)(x²+4). Real zeros: ±2.'
      },
      {
        id: 'poly-28',
        question: 'What is the intermediate value theorem used for?',
        options: ['Finding exact zeros', 'Proving a zero exists in an interval', 'Calculating derivatives', 'Determining end behavior'],
        correctAnswer: 1,
        explanation: 'IVT guarantees a zero exists between two points where the function changes sign.'
      },
      {
        id: 'poly-29',
        question: 'Which is NOT a polynomial function?',
        options: ['f(x) = 5x³ - 2x + 1', 'f(x) = x² + √3', 'f(x) = 2/x + 3', 'f(x) = 7'],
        correctAnswer: 2,
        explanation: 'f(x) = 2/x = 2x⁻¹ has a negative exponent, so it\'s not a polynomial.'
      },
      {
        id: 'poly-30',
        question: 'If f(x) = x³ + ax² + bx + c has zeros 1, 2, 3, what is c?',
        options: ['-6', '6', '-5', '5'],
        correctAnswer: 0,
        explanation: 'f(x) = (x-1)(x-2)(x-3) = x³ - 6x² + 11x - 6, so c = -6.'
      }
    ]
  },
  {
    id: 'precalc-2',
    title: 'Rational Functions',
    unit: 'Advanced Functions',
    description: 'Analyze rational functions including asymptotes, holes, domain restrictions, and graphing',
    standards: ['CCSS.MATH.CONTENT.HSF.IF.C.7d', 'NCTM: Algebra', 'Ohio: PC.F.2', 'NY: AI-F.IF.7d'],
    vocabulary: [
      { term: 'Rational Function', definition: 'A function that is the ratio of two polynomials: f(x) = P(x)/Q(x)' },
      { term: 'Vertical Asymptote', definition: 'A vertical line x = a where the function approaches ±∞' },
      { term: 'Horizontal Asymptote', definition: 'A horizontal line y = b that the function approaches as x → ±∞' },
      { term: 'Oblique Asymptote', definition: 'A slant asymptote that occurs when the numerator degree exceeds denominator by 1' },
      { term: 'Hole', definition: 'A removable discontinuity where both numerator and denominator equal zero' },
      { term: 'Domain', definition: 'All x-values where the function is defined (denominator ≠ 0)' },
      { term: 'Range', definition: 'All possible y-values the function can output' },
      { term: 'End Behavior', definition: 'The behavior of the function as x approaches ±∞' }
    ],
    concepts: [
      'Vertical asymptotes occur where the denominator equals zero (and numerator doesn\'t)',
      'Horizontal asymptotes depend on degree comparison: same degree → y = ratio of leading coefficients',
      'If numerator degree &lt; denominator degree, horizontal asymptote is y = 0',
      'If numerator degree &gt; denominator degree by 1, there\'s an oblique asymptote',
      'Holes occur at common factors in numerator and denominator',
      'Domain excludes all values that make the denominator zero'
    ],
    examples: [
      {
        problem: 'Find all asymptotes and holes of f(x) = (x² - 4)/(x² - x - 2)',
        steps: [
          'Factor numerator: (x - 2)(x + 2)',
          'Factor denominator: (x - 2)(x + 1)',
          'Common factor (x - 2) creates a hole at x = 2',
          'Simplified: f(x) = (x + 2)/(x + 1), x ≠ 2',
          'Vertical asymptote: x = -1',
          'Horizontal asymptote: y = 1 (same degree, ratio of leading coefficients)',
          'Hole at x = 2: y = (2+2)/(2+1) = 4/3, so hole at (2, 4/3)'
        ]
      },
      {
        problem: 'Find the horizontal asymptote of f(x) = (3x² - 5x + 1)/(2x² + 7)',
        steps: [
          'Compare degrees: numerator degree = 2, denominator degree = 2',
          'Same degree → horizontal asymptote is y = (leading coefficient of numerator)/(leading coefficient of denominator)',
          'y = 3/2',
          'As x → ±∞, f(x) → 3/2'
        ]
      }
    ],
    teacherTip: 'Have students use sign charts to analyze behavior near asymptotes. Understanding whether the function approaches +∞ or -∞ from each side helps with accurate graphing.',
    questions: [
      {
        id: 'rat-1',
        question: 'What is the domain of f(x) = 1/(x - 3)?',
        options: ['All real numbers', 'x ≠ 3', 'x ≠ -3', 'x &gt; 3'],
        correctAnswer: 1,
        explanation: 'The denominator cannot be zero, so x ≠ 3.'
      },
      {
        id: 'rat-2',
        question: 'Where is the vertical asymptote of f(x) = (x + 1)/(x - 5)?',
        options: ['x = -1', 'x = 1', 'x = -5', 'x = 5'],
        correctAnswer: 3,
        explanation: 'Vertical asymptote occurs where denominator = 0: x - 5 = 0, so x = 5.'
      },
      {
        id: 'rat-3',
        question: 'What is the horizontal asymptote of f(x) = (2x + 3)/(x - 1)?',
        options: ['y = 0', 'y = 1', 'y = 2', 'y = 3'],
        correctAnswer: 2,
        explanation: 'Same degree (both 1), so y = 2/1 = 2.'
      },
      {
        id: 'rat-4',
        question: 'What is the horizontal asymptote of f(x) = (3x)/(x² + 1)?',
        options: ['y = 0', 'y = 3', 'y = 1', 'No horizontal asymptote'],
        correctAnswer: 0,
        explanation: 'Numerator degree (1) &lt; denominator degree (2), so y = 0.'
      },
      {
        id: 'rat-5',
        question: 'Does f(x) = (x² + 1)/(x - 2) have a horizontal asymptote?',
        options: ['Yes, y = 0', 'Yes, y = 1', 'No, it has an oblique asymptote', 'Yes, y = 2'],
        correctAnswer: 2,
        explanation: 'Numerator degree (2) &gt; denominator degree (1) by 1, so there\'s an oblique asymptote.'
      },
      {
        id: 'rat-6',
        question: 'Where is the hole in f(x) = (x² - 9)/(x - 3)?',
        options: ['x = 3', 'x = -3', 'x = 9', 'No hole'],
        correctAnswer: 0,
        explanation: 'Factor: (x-3)(x+3)/(x-3). Common factor (x-3) creates hole at x = 3.'
      },
      {
        id: 'rat-7',
        question: 'What is the y-coordinate of the hole in f(x) = (x² - 9)/(x - 3)?',
        options: ['3', '6', '9', '0'],
        correctAnswer: 1,
        explanation: 'Simplify to (x+3), then substitute x=3: 3+3 = 6. Hole at (3, 6).'
      },
      {
        id: 'rat-8',
        question: 'What is the y-intercept of f(x) = (x + 2)/(x - 1)?',
        options: ['2', '-2', '1', '-1'],
        correctAnswer: 1,
        explanation: 'f(0) = (0+2)/(0-1) = 2/(-1) = -2.'
      },
      {
        id: 'rat-9',
        question: 'Where does f(x) = (x - 4)/(x + 1) cross the x-axis?',
        options: ['x = -1', 'x = 1', 'x = -4', 'x = 4'],
        correctAnswer: 3,
        explanation: 'Set numerator = 0: x - 4 = 0, so x = 4.'
      },
      {
        id: 'rat-10',
        question: 'How many vertical asymptotes does f(x) = 1/(x² - 4) have?',
        options: ['0', '1', '2', '3'],
        correctAnswer: 2,
        explanation: 'x² - 4 = (x-2)(x+2) = 0 at x = ±2. Two vertical asymptotes.'
      },
      {
        id: 'rat-11',
        question: 'What is the horizontal asymptote of f(x) = (5x³ + 2)/(x³ - 1)?',
        options: ['y = 0', 'y = 5', 'y = 2', 'y = -1'],
        correctAnswer: 1,
        explanation: 'Same degree (both 3), so y = 5/1 = 5.'
      },
      {
        id: 'rat-12',
        question: 'Which function has a hole at x = 2?',
        options: ['f(x) = (x-2)/(x+2)', 'f(x) = (x²-4)/(x-2)', 'f(x) = 1/(x-2)', 'f(x) = (x+2)/(x-3)'],
        correctAnswer: 1,
        explanation: 'f(x) = (x-2)(x+2)/(x-2) has common factor (x-2), creating a hole at x = 2.'
      },
      {
        id: 'rat-13',
        question: 'What is the range of f(x) = 1/x?',
        options: ['All real numbers', 'y ≠ 0', 'y &gt; 0', 'y &lt; 0'],
        correctAnswer: 1,
        explanation: '1/x can never equal 0, so the range is all real numbers except 0.'
      },
      {
        id: 'rat-14',
        question: 'As x → ∞, what does f(x) = (2x + 1)/(x - 3) approach?',
        options: ['0', '1', '2', '∞'],
        correctAnswer: 2,
        explanation: 'Horizontal asymptote y = 2/1 = 2, so f(x) → 2 as x → ∞.'
      },
      {
        id: 'rat-15',
        question: 'Which has NO horizontal asymptote?',
        options: ['f(x) = 1/x', 'f(x) = x/(x²+1)', 'f(x) = x²/(x+1)', 'f(x) = (2x+1)/(3x-2)'],
        correctAnswer: 2,
        explanation: 'f(x) = x²/(x+1): numerator degree (2) &gt; denominator degree (1), so no horizontal asymptote.'
      },
      {
        id: 'rat-16',
        question: 'What is the domain of f(x) = (x + 1)/(x² - 1)?',
        options: ['x ≠ 1', 'x ≠ -1', 'x ≠ ±1', 'All real numbers'],
        correctAnswer: 2,
        explanation: 'x² - 1 = (x-1)(x+1) = 0 when x = ±1, so domain is x ≠ ±1.'
      },
      {
        id: 'rat-17',
        question: 'After simplifying f(x) = (x² - 1)/(x² - 1), what is the function?',
        options: ['f(x) = 1', 'f(x) = 1, x ≠ ±1', 'f(x) = 0', 'Undefined everywhere'],
        correctAnswer: 1,
        explanation: 'Simplifies to 1, but original had restrictions x ≠ ±1.'
      },
      {
        id: 'rat-18',
        question: 'Where does f(x) = (x² - 4)/(x + 2) have a hole?',
        options: ['x = 2', 'x = -2', 'x = 4', 'No hole'],
        correctAnswer: 1,
        explanation: '(x-2)(x+2)/(x+2) has common factor (x+2), hole at x = -2.'
      },
      {
        id: 'rat-19',
        question: 'What is the oblique asymptote of f(x) = (x² + 1)/x?',
        options: ['y = x', 'y = x + 1', 'y = 1', 'y = 0'],
        correctAnswer: 0,
        explanation: 'Divide: (x² + 1)/x = x + 1/x. As x → ∞, 1/x → 0, so oblique asymptote is y = x.'
      },
      {
        id: 'rat-20',
        question: 'How many x-intercepts does f(x) = (x² - 5x + 6)/(x - 1) have?',
        options: ['0', '1', '2', '3'],
        correctAnswer: 2,
        explanation: 'Set numerator = 0: x² - 5x + 6 = (x-2)(x-3) = 0. Two x-intercepts: x = 2, 3.'
      },
      {
        id: 'rat-21',
        question: 'What happens to f(x) = 1/(x - 2) as x approaches 2 from the right?',
        options: ['Approaches 0', 'Approaches +∞', 'Approaches -∞', 'Approaches 2'],
        correctAnswer: 1,
        explanation: 'As x → 2⁺, denominator → 0⁺, so f(x) → +∞.'
      },
      {
        id: 'rat-22',
        question: 'What is the horizontal asymptote of f(x) = (4x² - 1)/(2x² + 3)?',
        options: ['y = 0', 'y = 1', 'y = 2', 'y = 4'],
        correctAnswer: 2,
        explanation: 'Same degree, so y = 4/2 = 2.'
      },
      {
        id: 'rat-23',
        question: 'Which function has vertical asymptotes at x = ±3?',
        options: ['f(x) = 1/(x² - 9)', 'f(x) = 1/(x² + 9)', 'f(x) = 1/(x - 3)', 'f(x) = (x² - 9)/x'],
        correctAnswer: 0,
        explanation: 'x² - 9 = (x-3)(x+3) = 0 at x = ±3.'
      },
      {
        id: 'rat-24',
        question: 'What is the domain of f(x) = √(x)/(x - 4)?',
        options: ['x ≥ 0', 'x ≠ 4', 'x ≥ 0, x ≠ 4', 'All real numbers'],
        correctAnswer: 2,
        explanation: 'Need x ≥ 0 for square root AND x ≠ 4 for denominator.'
      },
      {
        id: 'rat-25',
        question: 'If f(x) = (ax + b)/(cx + d) has a horizontal asymptote at y = 3, what is a/c?',
        options: ['1/3', '3', 'd/b', 'Cannot determine'],
        correctAnswer: 1,
        explanation: 'Horizontal asymptote for same degree is y = a/c, so a/c = 3.'
      },
      {
        id: 'rat-26',
        question: 'What is the vertical asymptote of f(x) = (2x + 1)/(3x - 6)?',
        options: ['x = -1/2', 'x = 1/2', 'x = 2', 'x = 6'],
        correctAnswer: 2,
        explanation: '3x - 6 = 0 when x = 2.'
      },
      {
        id: 'rat-27',
        question: 'Which rational function has NO vertical asymptote?',
        options: ['f(x) = 1/x', 'f(x) = (x² - 1)/(x - 1)', 'f(x) = x/(x² + 1)', 'f(x) = 1/(x² - 4)'],
        correctAnswer: 2,
        explanation: 'x² + 1 is never zero (always positive), so no vertical asymptote.'
      },
      {
        id: 'rat-28',
        question: 'What is the slant asymptote of f(x) = (x² + 2x + 1)/(x + 1)?',
        options: ['y = x', 'y = x + 1', 'y = x + 2', 'y = 2x'],
        correctAnswer: 1,
        explanation: 'Divide: (x² + 2x + 1)/(x + 1) = (x + 1)². Simplifies to x + 1 (with hole at x = -1).'
      },
      {
        id: 'rat-29',
        question: 'Where does f(x) = (x - 3)/(x + 2) equal zero?',
        options: ['x = -2', 'x = 2', 'x = -3', 'x = 3'],
        correctAnswer: 3,
        explanation: 'Set numerator = 0: x - 3 = 0, so x = 3.'
      },
      {
        id: 'rat-30',
        question: 'What is the range of f(x) = (x + 1)/(x - 1)?',
        options: ['All real numbers', 'y ≠ 0', 'y ≠ 1', 'y ≠ -1'],
        correctAnswer: 2,
        explanation: 'Horizontal asymptote is y = 1, which the function never reaches. Range: y ≠ 1.'
      }
    ]
  },
  {
    id: 'precalc-3',
    title: 'Exponential Functions',
    unit: 'Advanced Functions',
    description: 'Master exponential growth and decay, compound interest, and exponential modeling',
    standards: ['CCSS.MATH.CONTENT.HSF.IF.C.7e', 'NCTM: Algebra', 'Ohio: PC.F.3', 'NY: AI-F.IF.7e'],
    vocabulary: [
      { term: 'Exponential Function', definition: 'A function of the form f(x) = abˣ where b &gt; 0, b ≠ 1' },
      { term: 'Base', definition: 'The constant b in f(x) = abˣ' },
      { term: 'Growth Factor', definition: 'When b &gt; 1, the function represents exponential growth' },
      { term: 'Decay Factor', definition: 'When 0 &lt; b &lt; 1, the function represents exponential decay' },
      { term: 'Initial Value', definition: 'The value a in f(x) = abˣ, representing f(0)' },
      { term: 'Asymptote', definition: 'For f(x) = abˣ, the horizontal asymptote is y = 0' },
      { term: 'Compound Interest', definition: 'Interest calculated on initial principal and accumulated interest' },
      { term: 'Continuous Growth', definition: 'Growth modeled by f(t) = Aeʳᵗ where e ≈ 2.71828' }
    ],
    concepts: [
      'Exponential functions have the form f(x) = abˣ where a ≠ 0, b &gt; 0, b ≠ 1',
      'If b &gt; 1, the function grows exponentially; if 0 &lt; b &lt; 1, it decays',
      'The y-intercept is always (0, a)',
      'The horizontal asymptote is y = 0 (x-axis)',
      'Compound interest formula: A = P(1 + r/n)ⁿᵗ',
      'Continuous compound interest: A = Peʳᵗ',
      'Exponential functions are one-to-one (pass horizontal line test)'
    ],
    examples: [
      {
        problem: 'A population of 500 bacteria doubles every 3 hours. Write an exponential function and find the population after 12 hours.',
        steps: [
          'Initial value: a = 500',
          'Doubling means growth factor = 2',
          'Time period: every 3 hours, so use t/3 as exponent',
          'Function: P(t) = 500(2)^(t/3)',
          'After 12 hours: P(12) = 500(2)^(12/3) = 500(2)⁴',
          'P(12) = 500(16) = 8,000 bacteria'
        ]
      },
      {
        problem: 'Invest $5,000 at 6% annual interest compounded monthly. Find the value after 10 years.',
        steps: [
          'Use A = P(1 + r/n)ⁿᵗ',
          'P = 5000, r = 0.06, n = 12 (monthly), t = 10',
          'A = 5000(1 + 0.06/12)^(12×10)',
          'A = 5000(1.005)^120',
          'A ≈ 5000(1.8194) ≈ $9,097'
        ]
      }
    ],
    teacherTip: 'Use real-world contexts like population growth, radioactive decay, and compound interest to make exponential functions meaningful. Graphing technology helps students visualize rapid growth or decay.',
    questions: [
      {
        id: 'exp-1',
        question: 'What is the y-intercept of f(x) = 3(2)ˣ?',
        options: ['2', '3', '6', '0'],
        correctAnswer: 1,
        explanation: 'The y-intercept is f(0) = 3(2)⁰ = 3(1) = 3.'
      },
      {
        id: 'exp-2',
        question: 'Which function represents exponential decay?',
        options: ['f(x) = 2(3)ˣ', 'f(x) = 5(0.8)ˣ', 'f(x) = 4(1.2)ˣ', 'f(x) = 3(2)ˣ'],
        correctAnswer: 1,
        explanation: 'Decay occurs when 0 &lt; base &lt; 1. Only 0.8 satisfies this.'
      },
      {
        id: 'exp-3',
        question: 'What is the horizontal asymptote of f(x) = 5(2)ˣ?',
        options: ['y = 0', 'y = 2', 'y = 5', 'No asymptote'],
        correctAnswer: 0,
        explanation: 'All exponential functions f(x) = abˣ have horizontal asymptote y = 0.'
      },
      {
        id: 'exp-4',
        question: 'If f(x) = 100(1.05)ˣ, what is the growth rate?',
        options: ['1.05%', '5%', '50%', '105%'],
        correctAnswer: 1,
        explanation: 'Growth rate = (b - 1) × 100% = (1.05 - 1) × 100% = 5%.'
      },
      {
        id: 'exp-5',
        question: 'What is f(3) if f(x) = 2(3)ˣ?',
        options: ['6', '18', '27', '54'],
        correctAnswer: 3,
        explanation: 'f(3) = 2(3)³ = 2(27) = 54.'
      },
      {
        id: 'exp-6',
        question: 'A car depreciates at 15% per year. Which function models its value?',
        options: ['V(t) = V₀(1.15)ᵗ', 'V(t) = V₀(0.85)ᵗ', 'V(t) = V₀(0.15)ᵗ', 'V(t) = V₀(15)ᵗ'],
        correctAnswer: 1,
        explanation: '15% depreciation means retaining 85% = 0.85 of value each year.'
      },
      {
        id: 'exp-7',
        question: 'What is the domain of f(x) = 4(2)ˣ?',
        options: ['x &gt; 0', 'x ≥ 0', 'All real numbers', 'x ≠ 0'],
        correctAnswer: 2,
        explanation: 'Exponential functions are defined for all real numbers.'
      },
      {
        id: 'exp-8',
        question: 'What is the range of f(x) = 3(5)ˣ?',
        options: ['y &gt; 0', 'y ≥ 0', 'y &gt; 3', 'All real numbers'],
        correctAnswer: 0,
        explanation: 'Exponential functions with positive base have range y &gt; 0.'
      },
      {
        id: 'exp-9',
        question: 'If $1,000 is invested at 8% compounded annually, what is the value after 5 years?',
        options: ['$1,400', '$1,469', '$1,500', '$1,800'],
        correctAnswer: 1,
        explanation: 'A = 1000(1.08)⁵ ≈ 1000(1.469) ≈ $1,469.'
      },
      {
        id: 'exp-10',
        question: 'What is e approximately equal to?',
        options: ['2.71828', '3.14159', '1.61803', '2.30259'],
        correctAnswer: 0,
        explanation: 'Euler\'s number e ≈ 2.71828.'
      },
      {
        id: 'exp-11',
        question: 'Which function grows faster as x increases?',
        options: ['f(x) = 2ˣ', 'f(x) = 3ˣ', 'f(x) = x²', 'f(x) = 10x'],
        correctAnswer: 1,
        explanation: 'Among exponentials, larger base grows faster. 3ˣ &gt; 2ˣ for x &gt; 0.'
      },
      {
        id: 'exp-12',
        question: 'A population triples every 4 years. If initial population is 200, what is P(t)?',
        options: ['P(t) = 200(3)ᵗ', 'P(t) = 200(3)^(t/4)', 'P(t) = 200(4)^(t/3)', 'P(t) = 600(3)ᵗ'],
        correctAnswer: 1,
        explanation: 'Triples every 4 years: P(t) = 200(3)^(t/4).'
      },
      {
        id: 'exp-13',
        question: 'What is the decay factor if a substance loses 20% per hour?',
        options: ['0.2', '0.8', '1.2', '20'],
        correctAnswer: 1,
        explanation: 'Loses 20% means retains 80% = 0.8.'
      },
      {
        id: 'exp-14',
        question: 'If f(x) = 5(2)ˣ and g(x) = 5(3)ˣ, which grows faster?',
        options: ['f(x)', 'g(x)', 'Same rate', 'Cannot determine'],
        correctAnswer: 1,
        explanation: 'Larger base (3 &gt; 2) means faster growth.'
      },
      {
        id: 'exp-15',
        question: 'What is the continuous compound interest formula?',
        options: ['A = P(1 + r)ᵗ', 'A = Peʳᵗ', 'A = P(1 + r/n)ⁿᵗ', 'A = Prt'],
        correctAnswer: 1,
        explanation: 'Continuous compounding uses A = Peʳᵗ.'
      },
      {
        id: 'exp-16',
        question: 'If f(x) = abˣ passes through (0, 5) and (1, 15), what is b?',
        options: ['2', '3', '5', '10'],
        correctAnswer: 1,
        explanation: 'f(0) = a = 5. f(1) = 5b = 15, so b = 3.'
      },
      {
        id: 'exp-17',
        question: 'What is the half-life formula for exponential decay?',
        options: ['N(t) = N₀(1/2)^(t/h)', 'N(t) = N₀(2)^(t/h)', 'N(t) = N₀e^(-t/h)', 'Both A and C'],
        correctAnswer: 3,
        explanation: 'Both forms represent half-life decay correctly.'
      },
      {
        id: 'exp-18',
        question: 'If $2,000 grows to $2,500 in 3 years with continuous compounding at rate r, find r.',
        options: ['r ≈ 0.067', 'r ≈ 0.074', 'r ≈ 0.083', 'r ≈ 0.091'],
        correctAnswer: 1,
        explanation: '2500 = 2000e^(3r), so e^(3r) = 1.25, 3r = ln(1.25), r ≈ 0.074.'
      },
      {
        id: 'exp-19',
        question: 'Which is NOT an exponential function?',
        options: ['f(x) = 2ˣ', 'f(x) = x²', 'f(x) = (1/3)ˣ', 'f(x) = eˣ'],
        correctAnswer: 1,
        explanation: 'f(x) = x² is a power function, not exponential (variable is base, not exponent).'
      },
      {
        id: 'exp-20',
        question: 'What is the y-intercept of f(x) = 7(4)ˣ - 3?',
        options: ['4', '7', '4', '3'],
        correctAnswer: 0,
        explanation: 'f(0) = 7(4)⁰ - 3 = 7(1) - 3 = 4.'
      },
      {
        id: 'exp-21',
        question: 'If a population grows from 1,000 to 2,000 in 5 years, what is the annual growth rate?',
        options: ['14.87%', '20%', '25%', '100%'],
        correctAnswer: 0,
        explanation: '2000 = 1000(1+r)⁵, so (1+r)⁵ = 2, 1+r = 2^(1/5) ≈ 1.1487, r ≈ 14.87%.'
      },
      {
        id: 'exp-22',
        question: 'What is the horizontal asymptote of f(x) = 2(3)ˣ + 5?',
        options: ['y = 0', 'y = 2', 'y = 3', 'y = 5'],
        correctAnswer: 3,
        explanation: 'The +5 shifts the asymptote from y = 0 to y = 5.'
      },
      {
        id: 'exp-23',
        question: 'If f(x) = 100(0.9)ˣ, what percentage decreases each time?',
        options: ['9%', '10%', '90%', '0.9%'],
        correctAnswer: 1,
        explanation: 'Decay factor 0.9 means retaining 90%, so losing 10%.'
      },
      {
        id: 'exp-24',
        question: 'What is f(-2) if f(x) = 4(2)ˣ?',
        options: ['1', '2', '4', '8'],
        correctAnswer: 0,
        explanation: 'f(-2) = 4(2)⁻² = 4(1/4) = 1.'
      },
      {
        id: 'exp-25',
        question: 'Which investment earns more: $1,000 at 10% compounded annually for 10 years or $1,000 at 10% simple interest for 10 years?',
        options: ['Compound', 'Simple', 'Same', 'Cannot determine'],
        correctAnswer: 0,
        explanation: 'Compound: 1000(1.1)¹⁰ ≈ $2,594. Simple: 1000(1 + 0.1×10) = $2,000. Compound wins.'
      },
      {
        id: 'exp-26',
        question: 'What is the doubling time formula for exponential growth?',
        options: ['t = ln(2)/r', 't = 2/r', 't = r/ln(2)', 't = 2r'],
        correctAnswer: 0,
        explanation: 'For continuous growth A = Aeʳᵗ, doubling means 2 = eʳᵗ, so t = ln(2)/r.'
      },
      {
        id: 'exp-27',
        question: 'If f(x) = 3ˣ, what is f(x+1)/f(x)?',
        options: ['1', '3', 'x', '3x'],
        correctAnswer: 1,
        explanation: 'f(x+1)/f(x) = 3^(x+1)/3ˣ = 3ˣ·3/3ˣ = 3.'
      },
      {
        id: 'exp-28',
        question: 'A radioactive substance has a half-life of 8 days. What fraction remains after 24 days?',
        options: ['1/2', '1/4', '1/8', '1/16'],
        correctAnswer: 2,
        explanation: '24 days = 3 half-lives. Remaining = (1/2)³ = 1/8.'
      },
      {
        id: 'exp-29',
        question: 'What is the inverse of f(x) = 2ˣ?',
        options: ['f⁻¹(x) = x²', 'f⁻¹(x) = log₂(x)', 'f⁻¹(x) = ln(x)', 'f⁻¹(x) = x/2'],
        correctAnswer: 1,
        explanation: 'The inverse of an exponential function is a logarithm: f⁻¹(x) = log₂(x).'
      },
      {
        id: 'exp-30',
        question: 'If $5,000 is invested at 6% compounded quarterly, what is the value after 5 years?',
        options: ['$6,500', '$6,712', '$6,734', '$6,800'],
        correctAnswer: 2,
        explanation: 'A = 5000(1 + 0.06/4)^(4×5) = 5000(1.015)²⁰ ≈ $6,734.'
      }
    ]
  }
];